/* eslint-disable react/destructuring-assignment */
import TickMark from "@Icons/TickMark";
import NotFound from "@Pages/NotFound";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { isApp, isNewNativeApp } from "hooks/useIsApp";
import { ReactNode, PureComponent } from "react";

interface Props {
  children?: ReactNode;
  isComponentLevel?: boolean;
  noErrorLog?: boolean;
  componentName?: string;
  height?: string;
  addFallbackIcons?: boolean;
}
interface State {
  error: Error | null;
  errorInfo: { componentStack: string } | null;
}

class ErrorBoundary extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = { error: null, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }): void {
    // Catch errors in any components below and re-render with error message
    this.setState({ error, errorInfo });

    console.error(error);
    if (
      typeof window !== undefined &&
      typeof window.Sentry !== "undefined" &&
      !this.props.noErrorLog
    ) {
      let catchErr = error;
      if (this.props.componentName) {
        catchErr = new Error(`Error occurs in ${this.props.componentName} - ${error}`);
      }
      if (isApp() && typeof window !== "undefined") {
        sendWebViewEvent(WEB_VIEW_APP_EVENTS.WEB_CRASH, {
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          componentName: this.props.componentName || "UnknownComponent",
        });
      }
      window.Sentry.captureException(catchErr);
    }
    // You can also log error messages to an error reporting service here
  }

  render(): ReactNode {
    const { children, componentName, height, isComponentLevel } = this.props;
    const { errorInfo, error } = this.state;
    if (errorInfo) {
      if (this.props.addFallbackIcons) {
        return <TickMark />;
      }
      // RN app only. It consumes the `WEB_CRASH` bridge event and shows its own native crash
      // screen, so rendering web error UI underneath would double up.
      //
      // The new native app no longer listens to `WEB_CRASH` — see the `.webCrash` case in its
      // `WebViewBridge` — so the web must present its own fallback there, exactly as it does in
      // a browser. Returning nothing left the user on a blank WebView.
      if (isApp() && !isNewNativeApp()) {
        return <> </>;
      }
      if (__DEV__) {
        return (
          <div data-testid="error-view">
            <h2>Something went wrong.</h2>
            <details style={{ whiteSpace: "pre-wrap" }}>
              {error && error.toString()}
              <br />
              {errorInfo.componentStack}
            </details>
          </div>
        );
      }

      if (isComponentLevel || componentName) {
        return (
          <div
            data-testid="error-view"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: isComponentLevel ? "100%" : height,
            }}
          >
            {(isComponentLevel || height) && <h2>Something went wrong.</h2>}
          </div>
        );
      }
      return <NotFound isStatus500 />;
    }
    return children || null;
  }
}

export default ErrorBoundary;
